// @ts-check
import { Trans, Plural } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import groupBy from 'lodash/groupBy';
import React from 'react';
import {
  Flex,
  Link,
  NoSuccessIcon,
  SuccessIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  WarningIcon,
} from '~/design-system';
import getBottlebooksFieldsMap from '../MapFields/getBottlebooksFieldsMap';
/**
 * Renders the validation top table
 * @param {Object} props
 * @param {Object} props.sourceFields
 * @param {Object} props.bottlebooksFields
 * @param {Object} props.onShowColErrors
 * @param {Object} props.masterListColumns
 * @param {Object} props.isValid - indicates if there is any error on any field
 * @param {number} props.addedCount - the number of new products that will be added
 */
export default function FieldsTableValidation({
  sourceFields,
  bottlebooksFields,
  onShowColErrors,
  masterListColumns,
  isValid,
  addedCount,
  ...rest
}) {
  const { i18n } = useLingui();
  const isAddingNewProducts = addedCount > 0;
  const [columnErrorSelected, setColumnErrorSelected] = React.useState(null);
  const bottlebooksFieldsMap = getBottlebooksFieldsMap({
    bottlebooksFields,
    sourceFields,
    fieldsToImport: masterListColumns,
  });

  return (
    <Table
      sx={{
        border: '1px solid',
        borderColor: 'brand.lightGray',
        width: '100%',
      }}
      {...rest}
    >
      <TableHead>
        <TableRow>
          <TableHeader
            sx={{
              width: 90,
              backgroundColor: 'brand.lightGray',
              textAlign: 'center',
            }}
          >
            <Text variant="smallest">
              <Trans>Valid</Trans>
            </Text>
          </TableHeader>
          <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: 250 }}>
            <Text variant="smallest">
              <Trans>Bottlebooks field name</Trans>
            </Text>
          </TableHeader>
          <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: 250 }}>
            <Text variant="smallest">
              <Trans>Import column name</Trans>
            </Text>
          </TableHeader>
          <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: 250 }}>
            {isValid && isAddingNewProducts && (
              <Text variant="smallest">
                <Trans>Warning</Trans>
              </Text>
            )}
            {!isValid && (
              <Text variant="smallest">
                <Trans>Errors</Trans>
              </Text>
            )}
          </TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {masterListColumns.map((col) => {
          if (!bottlebooksFieldsMap?.[col.key]) return null;
          const isExternalIdField =
            bottlebooksFieldsMap[col.key]?.bottlebooksFieldName ===
            'product.productExternalId';

          const errors = bottlebooksFieldsMap[col.key]?.errors;
          const hasErrors = errors?.length > 0;
          const groupedErrors = groupBy(errors, 'code');

          return (
            <TableRow key={col.key}>
              <TableCell sx={{ height: '65px', textAlign: 'center' }}>
                {!hasErrors && isAddingNewProducts && isExternalIdField && (
                  <WarningIcon sx={{ color: 'brand.warningYellow' }} />
                )}
                {hasErrors && (
                  <NoSuccessIcon sx={{ color: 'brand.warningRed' }} />
                )}
                {!hasErrors && !isAddingNewProducts && (
                  <SuccessIcon sx={{ color: 'brand.successGreen' }} />
                )}
              </TableCell>
              <TableCell
                sx={{ height: '65px', position: 'relative', textAlign: 'left' }}
              >
                <Text variant="default">{i18n._(col.label)}</Text>
              </TableCell>
              {bottlebooksFieldsMap[col.key] && (
                <TableCell sx={{ height: '65px', textAlign: 'left' }}>
                  <Text>{bottlebooksFieldsMap[col.key]?.importFieldName}</Text>
                </TableCell>
              )}
              <TableCell
                sx={{ height: '65px', position: 'relative', textAlign: 'left' }}
              >
                {hasErrors && (
                  <Flex direction="column">
                    <GenericFieldErrorMessage
                      sx={{ marginTop: '1em' }}
                      groupedErrors={groupedErrors}
                      errors={errors}
                    />
                    {columnErrorSelected === col ? (
                      <Link
                        sx={{ marginLeft: 'auto', marginTop: '1em' }}
                        onClick={() => {
                          setColumnErrorSelected(null);
                          onShowColErrors(null);
                        }}
                      >
                        <Trans>Hide errors</Trans>
                      </Link>
                    ) : (
                      <Link
                        sx={{ marginTop: '1em' }}
                        onClick={() => {
                          setColumnErrorSelected(col);
                          onShowColErrors(col);
                        }}
                      >
                        {isAddingNewProducts && isExternalIdField ? (
                          <React.Fragment>
                            <Trans>Show {errors.length} rows.</Trans>
                          </React.Fragment>
                        ) : (
                          'Show errors'
                        )}
                      </Link>
                    )}
                  </Flex>
                )}
                {!hasErrors && isAddingNewProducts && isExternalIdField && (
                  <Text variant="smallest">
                    <Plural
                      value={addedCount}
                      one={
                        '1 product with unmatched external ID will be created and added to Portfolio.'
                      }
                      other={
                        '# products with unmatched external ID will be created and added to Portfolio.'
                      }
                    />
                  </Text>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function GenericFieldErrorMessage({ errors, groupedErrors, ...rest }) {
  const multipleErrorTypes = Object.keys(groupedErrors) > 1;
  const { code } = errors[0];

  if (multipleErrorTypes) {
    return (
      <Text variant="smallest" {...rest}>
        Multiple errors.
      </Text>
    );
  }
  if (code === 'objectId') {
    // TODO productExternalId doesn't have to be objectId
    return (
      <Text variant="smallest" {...rest}>
        Some values are not valid ID's.
      </Text>
    );
  }
  if (code === 'duplicate') {
    return (
      <Text variant="smallest" {...rest}>
        Some values are duplicated.
      </Text>
    );
  }
  if (code === 'not_found') {
    return (
      <Text variant="smallest" {...rest}>
        Some ids are missing in database.
      </Text>
    );
  }
  if (code === 'invalid_code') {
    return (
      <Text variant="smallest" {...rest}>
        Some codes are not valid.
      </Text>
    );
  }
  if (code === 'product_not_found') {
    return (
      <Text variant="smallest" {...rest}>
        Some products not found.
      </Text>
    );
  }
  if (code === 'question_not_found') {
    return (
      <Text variant="smallest" {...rest}>
        Question not found.
      </Text>
    );
  }
  if (code === 'internalIdsNotMigrated') {
    return (
      <Text variant="smallest" {...rest}>
        System error. Did you forget to run migration?
      </Text>
    );
  }
  if (code === 'notUniqueExternalIds') {
    return (
      <Text variant="smallest" {...rest}>
        Workspace contains not unique Product External IDs.
      </Text>
    );
  }
  if (code === 'UrlValidationError') {
    return (
      <Text variant="smallest" {...rest}>
        Some urls are not valid.
      </Text>
    );
  }
  if (code === 'EanValidationError' || code === 'GLNValidationError') {
    return (
      <Text variant="smallest" {...rest}>
        Some codes are not valid.
      </Text>
    );
  }

  if (code === 'FininAgentError') {
    return (
      <Text variant="smallest" {...rest}>
        There is some fining agent conflicts.
      </Text>
    );
  }
  if (code === 'PhoneNumberError') {
    return (
      <Text variant="smallest" {...rest}>
        Some phone numbers are invalid.
      </Text>
    );
  }
  if (code === 'ParentFieldNotSet') {
    return (
      <Text variant="smallest" {...rest}>
        Some related fields are missing.
      </Text>
    );
  }
  if (code === 'ParentFieldNotValid') {
    return (
      <Text variant="smallest" {...rest}>
        Some related fields are invalid.
      </Text>
    );
  }
  if (code === 'missingParentCode') {
    return (
      <Text variant="smallest" {...rest}>
        Some related fields are not defined.
      </Text>
    );
  }
  return <Text {...rest}>Not defined error.</Text>;
}
